// <algolia-recommend> - a custom, hand-rolled replacement for the official
// Algolia AI Search & Discovery app's "Recommend" block (see
// templates/*.algolia-test.json for the disabled app block this was modeled
// on - same settings vocabulary: model/maxRecommendations/minRecommendations
// /threshold/showVendor), built because the app block's own output can't be
// restyled to match this theme and ships no layout controls.
//
// Reuses the exact same building blocks as the collection grid/search
// results (algolia-client.js's AlgoliaClient, algolia-product-tile.js's
// renderProductTile) so a shopper sees one consistent product tile
// everywhere - just against Algolia's Recommend API
// (POST /1/indexes/*/recommendations) instead of Search.
import { AlgoliaClient } from "./algolia-client.js";
import { renderProductTile } from "./algolia-product-tile.js";
import { d as addItemById, a as contexts } from "./cart-cce84c00.js";
import { E as EVENTS } from "./events-58bc9098.js";

// Fires a Shopify Customer Events "custom event" - the supported theme-side
// hook into Web Pixels (window.Shopify.analytics.publish -> any pixel's
// analytics.subscribe("algolia_recommend_*", ...)). This is how these
// clicks/views/conversions reach GTM: see pixels/stape extended events.txt,
// which subscribes to the three event names below and relays them into the
// GTM web container's dataLayer via the same postMessage bridge it already
// uses for product_viewed/add_to_cart/etc - same single, consent-gated path
// every other Algolia signal on this site goes through (see
// algolia-client.js's publishAlgoliaEvent comment). Deliberately
// best-effort - a shopper without analytics consent, or a store with no
// pixel subscribed yet, just gets a no-op here.
function publishGtmEvent(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (e) {
    // Shopify.analytics is unavailable (e.g. theme editor preview) - no-op.
  }
}

function hitToGtmItem(hit, position) {
  return {
    objectID: hit.objectID,
    title: hit.title,
    vendor: hit.vendor,
    price: hit.price,
    position,
  };
}

// The anonymous user token search-insights persists when useCookie is on -
// the same token GTM's Insights tags attach to events. Reading it here lets
// the Recommend request carry a matching userToken so the request and its
// downstream events line up. Best-effort: on a first visit, before Insights
// has initialised, the cookie isn't there yet - we omit userToken and rely on
// the queryID alone to link events back to the recommendation.
function getInsightsUserToken() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ALGOLIA=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) {
    return null;
  }
}

class AlgoliaRecommend extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const eventOptions = { signal: this.controller.signal };

    const configEl = this.querySelector("[js-algolia-recommend-config]");
    if (!configEl) return;
    this.config = JSON.parse(configEl.textContent);

    if (!this.config.appId || !this.config.searchKey || !this.config.indexName) {
      console.warn("[algolia-recommend] missing Algolia app ID/search key/index name - not rendering.");
      this.classList.add("hidden");
      return;
    }

    // Anchor-based models (everything except trending-items) need the
    // current product's variant objectID - absent when this section is
    // placed somewhere other than a product template, or before a variant
    // is resolved. Rather than render an empty/broken widget, hide it, same
    // as <recommended-products> does when Shopify's own recommendation
    // endpoint comes back empty.
    if (this.config.requiresObjectId && !this.config.objectId) {
      this.classList.add("hidden");
      return;
    }

    // Delegated on `this` (the root custom element, never replaced - see
    // renderTiles) rather than the slider track itself, which does get
    // replaced wholesale once real tiles arrive.
    this.client = new AlgoliaClient({ appId: this.config.appId, searchKey: this.config.searchKey });
    // Recommend clicks are tracked via the product link's algolia_rec/oid/pos
    // params + the GTM "Wait for Tags" link-click tag (see renderTiles), so no
    // click listener is bound here - a Web-Pixel publish on click lost the event
    // to the page navigating away. Quick-add (converted) stays a delegate below
    // because add-to-cart doesn't navigate.
    this.bindQuickAddDelegate(eventOptions);

    this.observeAndFetch();
  }

  // Defer the Recommend API request until the widget is near the viewport.
  // These sections are almost always below the fold (PDP cross-sells, home-page
  // trending/related rows), so fetching on connect fired an Algolia request on
  // every home-page and product view whether or not the shopper ever scrolled
  // to it. A one-shot IntersectionObserver with a generous rootMargin kicks the
  // fetch off a little before the section scrolls into view, so tiles are ready
  // by the time it's reached - without the eager, mostly-wasted load. Falls
  // back to an immediate fetch where IntersectionObserver is unavailable.
  observeAndFetch() {
    if (!("IntersectionObserver" in window)) {
      this.fetchRecommendations();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.fetchRecommendations();
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(this);
    this.fetchObserver = observer;
  }

  async fetchRecommendations() {
    const request = {
      indexName: this.config.indexName,
      model: this.config.model,
      maxRecommendations: this.config.maxRecommendations,
      threshold: this.config.threshold,
    };
    if (this.config.objectId) request.objectID = this.config.objectId;
    if (this.config.model === "trending-items" && this.config.facetName && this.config.facetValue) {
      request.facetName = this.config.facetName;
      request.facetValue = this.config.facetValue;
    }

    // clickAnalytics makes the Recommend response return a queryID - REQUIRED
    // for Recommend analytics. Without it, click/conversion events can't be
    // attributed to the recommendation and CTR/CVR stay empty (Algolia, "Send
    // events for Recommend analytics"). Recommend reuses the Search event model,
    // so those events go to clickedObjectIDsAfterSearch / convertedObjectIDsAfter
    // Search carrying this queryID. (View events are tracked without a queryID.)
    request.queryParameters = { clickAnalytics: true };
    const userToken = getInsightsUserToken();
    if (userToken) request.queryParameters.userToken = userToken;

    try {
      const [result] = await this.client.getRecommendations([request]);
      // Captured once per fetch and reused for every tile's click link and the
      // quick-add conversion below - all tiles in this widget share the one
      // Recommend queryID.
      this.queryID = result?.queryID || null;
      // Anchor models normally exclude the seed product already, but
      // trending-items has no anchor to exclude by - filtering here (rather
      // than trusting Algolia) covers a shopper landing on trending-items
      // scoped to a facet the current product itself belongs to.
      const hits = (result?.hits || []).filter((hit) => hit.objectID !== this.config.objectId);

      if (hits.length < this.config.minRecommendations) {
        // Most common causes: this objectID genuinely has no recommendations
        // yet (too little Algolia event data for this model), or the
        // objectID sent doesn't match the index's record at all (e.g. a
        // type mismatch - Algolia objectIDs are always strings).
        console.warn(
          `[algolia-recommend] only ${hits.length} usable hit(s) for model "${this.config.model}" (objectID: ${this.config.objectId || "none"}, index: ${this.config.indexName}), minRecommendations is ${this.config.minRecommendations} - hiding section.`,
        );
        this.classList.add("hidden");
        return;
      }

      this.hits = hits;
      this.renderTiles(hits);
      this.observeViewed(hits);
      this.classList.remove("loading");
    } catch (e) {
      console.error("[algolia-recommend] recommendations request failed", e);
      this.classList.add("hidden");
    }
  }

  // scroll-slider.js (and scroll-slider-with-scroll-indicator.js, when the
  // scroll indicator is enabled) each cache their own child element
  // references exactly once, in connectedCallback, and never re-query them -
  // there's no public "refresh" API. Writing real tiles into the existing
  // .scroll-slider__slider via innerHTML would leave those caches pointing
  // at detached skeleton nodes forever (dead scroll/resize handling, no
  // pagination). Cloning the slider root (whichever one is present - see
  // algolia-recommend.liquid's .algolia-recommend__slider-anchor wrapper),
  // filling in the *clone's* track, then swapping it in wholesale forces a
  // full disconnect/reconnect of every custom element inside it, so they all
  // re-query fresh against the real tiles. Elements outside this subtree
  // (the nav buttons, this root element) are never touched.
  renderTiles(hits) {
    const anchor = this.querySelector(".algolia-recommend__slider-anchor");
    const oldRoot = anchor?.firstElementChild;
    if (!oldRoot) return;

    const newRoot = oldRoot.cloneNode(true);
    const track = newRoot.querySelector(".scroll-slider__slider");
    // Stamp each tile's product link with the Recommend click-attribution params
    // (algolia_rec/oid/pos). The GTM "Wait for Tags" link-click tag reads them
    // and sends clickedObjectIDs before navigation - reliable, unlike the old
    // Web-Pixel publish which lost the click to the PDP navigating away.
    const recommendEvent = `${this.config.modelLabel || "Recommend"} Clicked`;
    track.innerHTML = hits
      .map((hit, i) => renderProductTile(hit, this.config, i, { recommendEvent, queryID: this.queryID }))
      .join("");
    oldRoot.replaceWith(newRoot);
  }

  reportViewed(hits) {
    publishGtmEvent("algolia_recommend_viewed", {
      model: this.config.model,
      modelLabel: this.config.modelLabel,
      sourceObjectId: this.config.objectId,
      items: hits.map((hit, i) => hitToGtmItem(hit, i + 1)),
    });
  }

  // Recommendations fetch/render as soon as the widget connects, regardless
  // of scroll position (the section is frequently below the fold - PDP
  // cross-sells, footer-adjacent trending rows, etc.) - reporting "viewed"
  // at that same moment counted impressions the shopper never actually saw.
  // Deliberately a one-shot IntersectionObserver (disconnects after the
  // first trigger) rather than something re-firing on every scroll in/out -
  // Algolia's Insights "viewed" event means "shown once", not "currently
  // visible".
  observeViewed(hits) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.reportViewed(hits);
    });
    observer.observe(this);
    this.viewedObserver = observer;
  }

  // Deliberately a separate, small delegate rather than importing
  // algolia-product-tile.js's bindQuickAddDelegate: that one is hardwired to
  // look for an ancestor <algolia-collection-product-grid> and fire a
  // queryID-carrying GTM event - neither applies here (this element has no
  // queryID, and needs its own "algolia_recommend_converted" event instead).
  // Cart-adding logic itself is duplicated rather than parameterized, same
  // "small helpers duplicated per file" tradeoff already made between
  // algolia-collection-grid.js and algolia-product-tile.js.
  bindQuickAddDelegate(eventOptions) {
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[js-algolia-quick-add]");
        if (!button || button.disabled) return;

        const tile = button.closest("[data-algolia-objectid]");
        const position = tile ? Number(tile.dataset.algoliaPosition) : null;
        const hit = position != null ? this.hits?.[position - 1] : null;

        button.disabled = true;
        const originalLabel = button.textContent;
        button.textContent = "...";

        // addItemById()'s own promise resolves normally even on a 422 (sold
        // out, invalid/stale variant, etc.) - it never rejects, it just
        // dispatches a bubbling QUICK_ADD_ERROR event on the target element
        // passed in (cart-cce84c00.js's _handleError). The native
        // <product-item-quick-shopping> flow listens for that event to show
        // an inline error; this plain button has no such wrapper, so
        // without this listener a failed add was indistinguishable from a
        // successful one here - button reset, nothing in the cart, no error
        // visible anywhere.
        let addErrorMessage = null;
        const handleAddError = (errorEvent) => {
          addErrorMessage = errorEvent.detail?.errorMessage || "Couldn't add to cart";
        };
        button.addEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError, { once: true });

        addItemById(button.dataset.variantId, 1, contexts.productItem, button)
          .then(() => {
            if (addErrorMessage) {
              console.error("[algolia-recommend] add to cart failed:", addErrorMessage);
              return;
            }

            window.dispatchEvent(new CustomEvent(EVENTS.CART_DRAWER_OPENING));

            if (tile) {
              publishGtmEvent("algolia_recommend_converted", {
                model: this.config.model,
                modelLabel: this.config.modelLabel,
                sourceObjectId: this.config.objectId,
                // queryID from the Recommend response - sent to
                // convertedObjectIDsAfterSearch so the conversion is attributed
                // to this recommendation. null if the fetch returned no queryID.
                queryID: this.queryID || null,
                item: hit ? hitToGtmItem(hit, position) : { objectID: tile.dataset.algoliaObjectid, position },
              });
            }
          })
          .catch((e) => {
            console.error("[algolia-recommend] add to cart failed", e);
          })
          .finally(() => {
            button.removeEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError);
            button.disabled = false;
            if (addErrorMessage) {
              // The button is a fixed-width, white-space:nowrap pill sized
              // for "Add to cart" - Shopify's real error text (often a full
              // sentence) just overflows it. Show a short, constant label
              // here; the actual message is already in the console.error
              // above for whoever's debugging.
              button.textContent = "Unavailable";
              setTimeout(() => {
                button.textContent = originalLabel;
              }, 2500);
              return;
            }
            button.textContent = originalLabel;
          });
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.fetchObserver?.disconnect();
    this.viewedObserver?.disconnect();
  }
}

if (!customElements.get("algolia-recommend")) {
  customElements.define("algolia-recommend", AlgoliaRecommend);
}